/**
 * Downshift Scanner Page - Regime-Aware Version (Redesigned)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { TrendingDown, TrendingUp, SwapHoriz } from '@mui/icons-material';
import { downshiftScannerAPI } from '../services/api';
import { DEFAULT_CONFIG } from '../components/downshift-scanner/constants';
import ScannerHeader from '../components/downshift-scanner/ScannerHeader';
import ConfigPanel from '../components/downshift-scanner/ConfigCards';
import StatsCards from '../components/downshift-scanner/StatsCards';
import StockGrid from '../components/downshift-scanner/StockGrid';

const TAB_CONFIG = [
  { key: 'distribution', label: 'Phân Phối', color: '#dc2626', Icon: TrendingDown },
  { key: 'accumulation', label: 'Tích Lũy', color: '#16a34a', Icon: TrendingUp },
  { key: 'transition', label: 'Chuyển Giao', color: '#ca8a04', Icon: SwapHoriz },
];

const DownshiftScanner = () => {
  const [loading, setLoading] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [error, setError] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [activeTab, setActiveTab] = useState(0);
  const didInitialFetch = useRef(false);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await downshiftScannerAPI.scanRegime({
        tickers: null,
        min_avg_value_20: config.min_avg_value_20,
        min_price: config.min_price,
        limit: config.limit,
        distribution_threshold: config.distribution_threshold,
        accumulation_threshold: config.accumulation_threshold,
        use_obv_divergence: config.use_obv_divergence,
        require_weekly_confirmation: config.require_weekly_confirmation,
      });
      setScanData(response.data);
      setLastScan(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    if (!didInitialFetch.current) {
      didInitialFetch.current = true;
      fetchResults();
    }
  }, [fetchResults]);

  const distributionRisks = scanData?.distribution_risks || [];
  const accumulationCandidates = scanData?.accumulation_candidates || [];
  const transitionStocks = scanData?.transition_stocks || [];
  const counts = [distributionRisks.length, accumulationCandidates.length, transitionStocks.length];
  const datasets = [distributionRisks, accumulationCandidates, transitionStocks];

  return (
    <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
      <ScannerHeader loading={loading} lastScan={lastScan} onScan={fetchResults} />

      <ConfigPanel config={config} onConfigChange={setConfig} />

      <StatsCards
        distributionCount={counts[0]}
        accumulationCount={counts[1]}
        transitionCount={counts[2]}
        loading={loading && !scanData}
        onSelect={(idx) => setActiveTab(idx)}
      />

      {/* Tab bar */}
      <Box sx={{
        display: 'flex', gap: 0, mb: 0,
        borderBottom: '2px solid #e2e8f0',
      }}>
        {TAB_CONFIG.map((tab, idx) => {
          const active = activeTab === idx;
          return (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(idx)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.6,
                px: 2, py: 1, cursor: 'pointer',
                borderBottom: active ? `2px solid ${tab.color}` : '2px solid transparent',
                mb: '-2px',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: alpha(tab.color, 0.04) },
              }}
            >
              <tab.Icon sx={{ fontSize: 16, color: active ? tab.color : '#94a3b8' }} />
              <Typography sx={{
                fontSize: '0.78rem', fontWeight: active ? 800 : 600,
                color: active ? tab.color : '#64748b',
                transition: 'all 0.15s',
              }}>
                {tab.label}
              </Typography>
              <Box sx={{
                display: 'inline-flex', px: 0.6, py: 0.1, borderRadius: 1,
                bgcolor: active ? alpha(tab.color, 0.1) : '#f1f5f9',
                minWidth: 20, justifyContent: 'center',
              }}>
                <Typography sx={{
                  fontSize: '0.65rem', fontWeight: 800, fontFamily: 'monospace',
                  color: active ? tab.color : '#94a3b8',
                }}>
                  {counts[idx]}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Tab content */}
      <Box sx={{
        borderRadius: '0 0 8px 8px',
        border: '1px solid #e2e8f0', borderTop: 'none',
        bgcolor: '#fff', overflow: 'hidden',
      }}>
        <StockGrid
          data={datasets[activeTab]}
          loading={loading}
          error={error}
          variant={TAB_CONFIG[activeTab].key}
        />
      </Box>
    </Box>
  );
};

export default DownshiftScanner;
